<template>
    <UserTabs
        :selected-user="selectedUser"
        :personal-team="userPersonalTeam"
        tab-key="feature-management"
    >
        <div class="mt-12 rounded-md bg-white p-6 text-gray-900 shadow-md">
            <div class="grid grid-cols-4">
                <div>
                    <div>
                        <div class="text-base font-medium text-gray-700">
                            {{ personalTeam.name }}
                        </div>

                        <div
                            v-for="(newFeature, index) in unusedNewFeatures(
                                newFeatures,
                                featuresArray(personalTeam.features),
                            )"
                            :key="index"
                            class="my-2 flex items-center"
                        >
                            <div class="mr-4 text-sm text-gray-600">
                                {{ newFeature.value }}:
                            </div>

                            <CheckboxInput
                                v-model:checked="newFeature.checked"
                                size="md"
                                :name="newFeature.value"
                                @change="
                                    updateFeature(newFeature, personalTeam)
                                "
                            />
                        </div>

                        <div v-if="personalTeam.features">
                            <div
                                v-for="(feature, index) in featuresArray(
                                    personalTeam.features,
                                )"
                                :key="index"
                                class="my-2 flex items-center"
                            >
                                <div class="mr-4 text-sm text-gray-600">
                                    {{ feature.value }}:
                                </div>

                                <CheckboxInput
                                    v-model:checked="feature.checked"
                                    size="md"
                                    :name="feature.value"
                                    @change="
                                        updateFeature(feature, personalTeam)
                                    "
                                />
                            </div>
                        </div>

                        <div class="mt-6">
                            <label
                                for="trial_ends_at"
                                class="block text-sm font-medium text-gray-700 mb-1"
                            >
                                {{ t("vue.trial_expires") }}:
                            </label>

                            <input
                                id="trial_ends_at"
                                v-model="trialEndsAtLocal"
                                type="datetime-local"
                                class="border border-gray-300 rounded-md p-2 w-full"
                                @change="updateTrialEndsAt"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </UserTabs>
</template>

<script lang="ts" setup>
import type IUser from "@/types/Members/IUser";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { ref, watch } from "vue";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

interface IFeature {
    value: string;
    checked: boolean;
}

const props = defineProps<{
    selectedUser: IUser;
    personalTeam: ICurrentTeam;
    newFeatures: IFeature[];
    userPersonalTeam: ICurrentTeam;
}>();

const updateFeature = (feature: IFeature, team: ICurrentTeam) => {
    router.put(
        route("kiosk.users.feature-management.update", {
            team: team.id,
            feature: feature.value,
            checked: feature.checked,
        }),
        {},
        { preserveScroll: true },
    );
};

const unusedNewFeatures = (newFeatures: IFeature[], features: IFeature[]) => {
    return newFeatures.filter(
        (newFeature) =>
            !features.some((feature) => feature.value === newFeature.value),
    );
};

const featuresArray = (featureArr: object | undefined | null) => {
    const features = [] as IFeature[];
    if (featureArr === undefined || featureArr === null) {
        return features;
    }
    Object.entries(featureArr).forEach(([key, value]) =>
        features.push({ value: key, checked: value }),
    );
    return features;
};

const trialEndsAtLocal = ref<string>("");

if (props.selectedUser.trial_ends_at) {
    const date = new Date(props.selectedUser.trial_ends_at);
    const pad = (n: number) => String(n).padStart(2, "0");
    trialEndsAtLocal.value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const updateTrialEndsAt = () => {
    if (!trialEndsAtLocal.value) return;

    const localDate = new Date(trialEndsAtLocal.value);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");
    const hours = String(localDate.getHours()).padStart(2, "0");
    const minutes = String(localDate.getMinutes()).padStart(2, "0");
    const seconds = String(localDate.getSeconds()).padStart(2, "0");

    const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

    router.put(
        route("kiosk.users.feature-management.update", {
            team: props.personalTeam.id,
        }),
        { trial_ends_at: formatted },
        { preserveScroll: true },
    );
};
</script>
